import { EventSummaryDTO } from "../types/event.types";
import {
  CreateRegistrationDTO,
  UpdateRegistrationDTO,
  RegistrationResponse,
  RegistrationListResponse,
  CheckInResponse,
  RegistrationStatsResponse,
} from "../types/registration.types";
import { ApiResponse, EventRegistration } from "../types/shared.types";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const userId = sessionStorage.getItem("userOid");
  if (userId) headers["client-principal-user-id"] = userId;

  const email = sessionStorage.getItem("userEmail");
  if (email)
    headers["client-principal-user-email"] = email.trim().toLowerCase();

  const name = sessionStorage.getItem("userName");
  if (name) headers["client-principal-user-name"] = name;

  return headers;
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || "Request failed");
  }
  return data;
};

export const getEventForRegistration = async (
  eventId: string,
): Promise<ApiResponse<EventSummaryDTO>> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/summary`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const registerForEvent = async (
  eventId: string,
  data: Omit<CreateRegistrationDTO, "eventId">,
  isUpdate: boolean = false,
): Promise<RegistrationResponse> => {
  try {
    const endpoint = isUpdate
      ? `${API_BASE}/registrations/${eventId}/update`
      : `${API_BASE}/registrations/${eventId}/register`;

    const method = isUpdate ? "PUT" : "POST";

    const response = await fetch(endpoint, {
      method,
      headers: getHeaders(),
      body: JSON.stringify({
        ...data,
        userEmail: data.userEmail.trim().toLowerCase(),
      }),
    });

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const checkMyRegistrationStatus = async (
  eventId: string,
): Promise<{
  success: boolean;
  data?: { isRegistered: boolean; registration?: any };
  error?: string;
}> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/my-status`,
      { method: "GET", headers: getHeaders() },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const updateOwnRegistration = async (
  eventId: string,
  email: string,
  updates: UpdateRegistrationDTO,
): Promise<RegistrationResponse> => {
  try {
    const normalizedEmail = email.trim().toLowerCase();
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/update`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ email: normalizedEmail, ...updates }),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const getEventRegistrations = async (
  eventId: string,
  options?: {
    status?: string;
    includeDeclined?: boolean;
  },
): Promise<RegistrationListResponse> => {
  try {
    const params = new URLSearchParams();
    if (options?.status) params.append("status", options.status);
    if (options?.includeDeclined) params.append("includeDeclined", "true");

    const response = await fetch(
      `${API_BASE}/registrations/${eventId}?${params.toString()}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const getRegistrationStats = async (
  eventId: string,
): Promise<RegistrationStatsResponse> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/stats`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const getAttendanceList = async (
  eventId: string,
): Promise<{
  success: boolean;
  count?: number;
  data?: Array<{
    name: string;
    email: string;
    status: string;
    mealPreference: string;
    checkInTime?: string;
  }>;
  error?: string;
}> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/attendance`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const getRegistrationById = async (
  eventId: string,
  registrationId: string,
): Promise<RegistrationResponse> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/${registrationId}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const updateRegistration = async (
  eventId: string,
  registrationId: string,
  updates: UpdateRegistrationDTO,
): Promise<RegistrationResponse> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/${registrationId}`,
      {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(updates),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const deleteRegistration = async (
  eventId: string,
  registrationId: string,
): Promise<{ success: boolean; error?: string; message?: string }> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/${eventId}/${registrationId}`,
      {
        method: "DELETE",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const checkInByScan = async (
  checkInCode: string,
): Promise<CheckInResponse> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/check-in/scan`,
      {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ checkInCode }),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const checkInManually = async (
  eventId: string,
  registrationId: string,
): Promise<CheckInResponse> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/check-in/${eventId}/${registrationId}`,
      {
        method: "POST",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const undoCheckIn = async (
  eventId: string,
  registrationId: string,
): Promise<RegistrationResponse> => {
  try {
    const response = await fetch(
      `${API_BASE}/registrations/check-in/${eventId}/undo/${registrationId}`,
      {
        method: "POST",
        headers: getHeaders(),
      },
    );

    return await handleResponse(response);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: message };
  }
};

export const getMyRegistrations =
  async (): Promise<RegistrationListResponse> => {
    try {
      const response = await fetch(
        `${API_BASE}/registrations/me/registrations`,
        {
          method: "GET",
          headers: getHeaders(),
        },
      );

      return await handleResponse(response);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      return { success: false, error: message };
    }
  };

export const getCheckInQRUrl = (checkInCode: string): string => {
  const frontendUrl =
    process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  return `${frontendUrl}/check-in/${checkInCode}`;
};

export const exportRegistrationsToCSV = (
  registrations: EventRegistration[],
  eventName: string,
): void => {
  const headers = [
    "Name",
    "Email",
    "Status",
    "Meal Preference",
    "Registration Date",
    "Check-in Time",
  ];

  const rows = registrations.map((r) => [
    r.userName,
    r.userEmail,
    r.status,
    r.mealPreference,
    new Date(r.registrationDate).toLocaleString("de-DE"),
    r.checkInTime ? new Date(r.checkInTime).toLocaleString("de-DE") : "",
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${eventName.replace(/[^a-z0-9]/gi, "_")}_registrations.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
